import React, { useState, useEffect, useRef, useMemo } from "react";
import { findDOMNode } from "react-dom";
import Icon from "../../Elements/Icon/Icon.tsx";
import screenfull from "screenfull";
import useFileData from "./useFileData.js";
import AnimationContainer from "../AnimationContainer/AnimationContainer.js";
import audioTimeViewTransfrom from "../../../utils/audioTimeViewTransfrom.js";
import Progressbar from "../Progressbar/Progressbar.js";
import ReactPlayer from "react-player/lazy/index.js";
import { useMail } from "contexts/mailContext/mailContext.js";

const VideoComponent = ({ id, feedView, lightBox }) => {
  const [play, setPlay] = useState(true);
  const [volume, setVolume] = useState(0.8);
  const [currentTime, setCurrentTime] = useState(0);
  const [progress, setProgress] = useState(0);
  const [wasClicked, setWasClicked] = useState(false);
  const [volumeBarOpen, setVolumeBarOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const fileData = useFileData(id);
  const { setFilePlaying, getFilePlaying } = useMail();

  // check if getFIlePlaying was changed and if its not the current file then pause
  useEffect(() => {
    if (getFilePlaying() !== id && getFilePlaying()) {
      setPlay(false);
    }
  }, [getFilePlaying()]);

  // if this is item is in feed view then pause on mount
  useMemo(() => {
    if (feedView) {
      setPlay(false);
    }
  }, []);

  // HANDLERS FUNCTIONS

  const handleClickFullscreen = () => {
    screenfull.request(findDOMNode(playerRef.current));
  };

  const handlePlay = (e) => {
    e.stopPropagation();
    if (!play) setFilePlaying(id);
    setPlay((prev) => !prev);
  };

  const handlePlayPauseSpaceBar = (e) => {
    if (e.keyCode == 32) {
      handlePlay(e);
    }
  };

  const handleProgress = (obj) => {
    setCurrentTime(obj.playedSeconds);
    setProgress(parseInt(obj.played * 100));
  };

  const handleVolumeSeek = (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left < 0 ? 0 : e.clientX - rect.left;
    setVolume(x / rect.width);
  };

  const handleProgressSeek = (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const fraction = x / rect.width;
    setProgress(parseInt(fraction * 100));
    playerRef.current.seekTo(fraction, "fraction");
  };

  const hanldeMouseMove = (e) => {
    const videoRect = document
      .querySelector(".player-container")
      ?.getClientRects();
    if (
      videoRect &&
      videoRect.x < e.clientX < videoRect.x + videoRect.width &&
      videoRect.y < e.clientY < videoRect.y + videoRect.height
    ) {
      setHovered(true);
    }
    window.removeEventListener("mousemove", hanldeMouseMove);
  };

  useEffect(() => {
    window.addEventListener("mousemove", hanldeMouseMove);
    document.body.addEventListener("keyup", handlePlayPauseSpaceBar);
    return () => {
      window.removeEventListener("mousemove", hanldeMouseMove);
      document.body.removeEventListener("keyup", handlePlayPauseSpaceBar);
    };
  }, []);

  const playerRef = useRef();

  return (!id || feedView || lightBox) && fileData.PreviewSrc ? (
    <div className="video-container">
      <AnimationContainer animationOn={wasClicked} animationClassName="fade-in">
        <div className="play-icon">
          <Icon
            name={play ? "pause_icon" : "play"}
            white
            onClick={handlePlay}
          />
        </div>
      </AnimationContainer>
      <div
        className="player-container"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={(e) => {
          handlePlay(e);
          setWasClicked(true);
          setTimeout(() => {
            setWasClicked(false);
          }, 500);
        }}
      >
        <ReactPlayer
          ref={playerRef}
          progressInterval={10}
          pip={true}
          url={fileData.PreviewSrc}
          playing={play}
          volume={volume}
          onProgress={handleProgress}
          height="100%"
          width="100%"
          style={{
            maxHeight: "100vh",
            maxWidth: "100vw",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
          onEnded={() => setPlay(false)}
        />
        <AnimationContainer
          animationOn={hovered || !play}
          animationClassName="hover-item-fade"
        >
          <div className="video-controls-background" />
          <div className="video-controls">
            <Icon
              name={play ? "pause_icon" : "play"}
              className="control-play-pause"
              white
              onClick={handlePlay}
            />
            <div
              className={`audio-volume-range ${volumeBarOpen ? "opened" : ""}`}
              onMouseLeave={() => setVolumeBarOpen(false)}
            >
              <Icon
                name={`Speaker-volume-${!volume ? "off" : "high"}-01`}
                white
                onMouseEnter={(e) => {
                  e.stopPropagation();
                  setVolumeBarOpen(true);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setVolume(volume ? 0 : 0.8);
                }}
              />
              <Progressbar
                completed={volume * 100}
                onClick={handleVolumeSeek}
                color="#15B4D4"
                height="12px"
                animation="100"
              />
            </div>
            <div className="video-timing">
              {audioTimeViewTransfrom(currentTime) || 0}
            </div>
            <Progressbar
              completed={progress}
              onClick={handleProgressSeek}
              color="#15B4D4"
              height="12px"
              animation="100"
            />
            <div className="video-timing">
              {playerRef.current
                ? audioTimeViewTransfrom(playerRef.current.getDuration())
                : 0}
            </div>
            <Icon
              name="full-screen"
              onClick={(e) => {
                e.stopPropagation();
                handleClickFullscreen();
              }}
              white
            />
          </div>
        </AnimationContainer>
      </div>
    </div>
  ) : (
    <div className="square-box">
      <div className="doc-box">
        <Icon name="file" gray />
        <div className="file-extension">
          <span>{fileData.Extention}</span>
        </div>
      </div>
    </div>
  );
};

VideoComponent.defaultProps = {
  autoPlay: true,
};

export default VideoComponent;
